import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    ADMIN_NAV_GROUPS,
    adminMobileMoreNavGroups,
    adminNavItemIsActive,
    adminNavTargetIsActive,
    type AdminNavItem,
} from '@/constants/admin-nav';

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    isMobile: boolean;
    mobileOpen: boolean;
    onMobileClose: () => void;
}

function SidebarLeaf({
    item,
    showLabels,
    active,
    onNavigate,
}: {
    item: AdminNavItem;
    showLabels: boolean;
    active: boolean;
    onNavigate: () => void;
}) {
    const Icon = item.icon;
    return (
        <NavLink
            to={item.path}
            onClick={onNavigate}
            className={cn(
                'flex items-center gap-3 rounded-lg text-sm font-medium',
                'transition-colors duration-150 group relative',
                showLabels ? 'px-3 py-2' : 'justify-center p-2.5 mx-auto',
                active
                    ? 'bg-sidebar-active text-primary'
                    : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-foreground'
            )}
        >
            {active && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary rounded-r-full" />
            )}
            <span className={cn('shrink-0', active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')}>
                <Icon size={20} />
            </span>
            {showLabels && <span className="truncate animate-fade-in">{item.label}</span>}
            {!showLabels && (
                <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-popover text-popover-foreground text-xs font-medium rounded-md shadow-lg border border-border opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                    {item.label}
                </div>
            )}
        </NavLink>
    );
}

function SidebarBranch({
    item,
    showLabels,
    onNavigate,
}: {
    item: AdminNavItem;
    showLabels: boolean;
    onNavigate: () => void;
}) {
    const { pathname } = useLocation();
    const active = adminNavItemIsActive(pathname, item);
    const [userOpen, setUserOpen] = useState(false);
    const sections = item.sections ?? [];
    const open = active || userOpen;
    const Icon = item.icon;

    if (!sections.length || !showLabels) {
        return (
            <SidebarLeaf
                item={item}
                showLabels={showLabels}
                active={active}
                onNavigate={onNavigate}
            />
        );
    }

    return (
        <div>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                    if (!active) setUserOpen((current) => !current);
                }}
                className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-left',
                    'transition-colors duration-150 group relative',
                    active
                        ? 'text-primary'
                        : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-foreground'
                )}
            >
                <span className={cn('shrink-0', active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')}>
                    <Icon size={20} />
                </span>
                <span className="truncate animate-fade-in">{item.label}</span>
                <ChevronDown
                    size={16}
                    className={cn('ml-auto shrink-0 text-muted-foreground transition-transform', open ? '' : '-rotate-90')}
                />
            </button>
            {open && (
                <div className="mt-0.5 mb-1">
                    {sections.map((section) => (
                        <div key={section.label} className="mt-2">
                            <div className="px-3 mb-0.5">
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                                    {section.label}
                                </span>
                            </div>
                            {section.items.map((child) => {
                                const childActive = adminNavTargetIsActive(pathname, child);
                                return (
                                    <NavLink
                                        key={child.path}
                                        to={child.path}
                                        onClick={onNavigate}
                                        className={cn(
                                            'flex items-center rounded-lg pl-9 pr-3 py-1.5 text-sm font-medium relative',
                                            'transition-colors duration-150',
                                            childActive
                                                ? 'bg-sidebar-active text-primary'
                                                : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-foreground'
                                        )}
                                    >
                                        {childActive && (
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-primary rounded-r-full" />
                                        )}
                                        <span className="truncate">{child.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function Sidebar({ collapsed, onToggle, isMobile, mobileOpen, onMobileClose }: SidebarProps) {
    const showLabels = isMobile || !collapsed;
    const groups = isMobile ? adminMobileMoreNavGroups() : ADMIN_NAV_GROUPS;

    return (
        <aside
            className={cn(
                'fixed top-0 left-0 h-screen bg-sidebar border-r border-sidebar-border',
                'flex flex-col transition-sidebar overflow-hidden',
                isMobile
                    ? cn(
                        'z-40 w-[min(var(--sidebar-width),85vw)] shadow-2xl',
                        'pt-[var(--safe-area-top)]',
                        mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
                    )
                    : cn(
                        'z-30 translate-x-0',
                        collapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]'
                    )
            )}
            aria-hidden={isMobile && !mobileOpen}
        >
            <div className="flex items-center h-[var(--topbar-height)] px-4 border-b border-sidebar-border shrink-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <img
                        src="/aipeces-mark-dark-ui.png"
                        alt="AiPeces"
                        className="w-8 h-8 rounded-lg shrink-0 object-contain"
                    />
                    {showLabels && (
                        <div className="flex flex-col min-w-0 animate-fade-in">
                            <span className="text-sm font-bold text-foreground tracking-tight truncate">AiPeces</span>
                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Admin</span>
                        </div>
                    )}
                </div>
                {isMobile && (
                    <button
                        type="button"
                        onClick={onMobileClose}
                        className="p-2 -mr-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-sidebar-hover transition-colors"
                        aria-label="Cerrar menú"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
                {groups.map((group) => (
                    <div key={group.title} className="mb-4">
                        {showLabels && (
                            <div className="px-3 mb-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                                    {group.title}
                                </span>
                            </div>
                        )}
                        <div className="space-y-0.5">
                            {group.items.map((item) => (
                                <SidebarBranch
                                    key={item.path}
                                    item={item}
                                    showLabels={showLabels}
                                    onNavigate={() => {
                                        if (isMobile) onMobileClose();
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {!isMobile && (
                <button
                    type="button"
                    onClick={onToggle}
                    className={cn(
                        'flex items-center justify-center h-10 mx-2 mb-2 rounded-lg',
                        'text-muted-foreground hover:text-foreground hover:bg-sidebar-hover',
                        'transition-colors duration-150'
                    )}
                    title={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
                >
                    {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </button>
            )}
        </aside>
    );
}
